<script setup lang="ts">
import { computed } from 'vue';

import type { AccommodationSelectorType } from '@apps/main/new-desktop/modules/accommodationSelector';
import { AccommodationSelector } from '@apps/main/new-desktop/modules/accommodationSelector';
import { i18n } from '@apps/main/modules/localization/localizationSetup';
import type { HotelFilter } from '@apps/main/new-desktop/modules/hotelFilters';
import {
  useBrowserStore,
  useStatsigEventsStore,
} from '@apps/main/new-desktop/core/stores';
import { useFiltersStore } from '@apps/main/new-desktop/core/stores/filters/filtersStore';
import { useFlightsFiltersStore } from '@apps/main/new-desktop/core/stores/filters/flightsFiltersStore';
import { useFerryFiltersStore } from '@apps/main/new-desktop/core/stores/filters/ferryFiltersStore';

import { AppFlicker, AppSingleSelect } from '@libs/ui';
import { TransportType } from '@libs/trip-planner-interfaces';

import ActivityCardHeader from '../acitivity/ActivityCardHeader.vue';
import ItineraryTimeline from '../ItineraryTimeline.vue';
import ItineraryTransportSelector from '../ItineraryTransportSelector.vue';
import ChangeProductLink from '../ChangeProductLink.vue';
import type { ItineraryBookableProductLoaderSegment } from '../../../models';
import {
  isItineraryBookableTransportLoaderSegment,
  ItinerarySegmentType,
  ProductCardTopBarType,
} from '../../../models';
import {
  useTripOverviewPageStore,
  useTripOverviewPanelsStore,
} from '../../../stores';
import AccommodationSegmentHeader from '../accommodation/AccommodationSegmentHeader.vue';
import FlightSegmentHeader from '../flight/FlightSegmentHeader.vue';
import FerrySegmentHeader from '../ferry/FerrySegmentHeader.vue';

interface ItineraryProductLoaderSegmentProps {
  segment: ItineraryBookableProductLoaderSegment;
}
const props = defineProps<ItineraryProductLoaderSegmentProps>();

const tripOverviewPageStore = useTripOverviewPageStore();
const statsigEventsStore = useStatsigEventsStore();
const tripOverviewPanelsStore = useTripOverviewPanelsStore();
const filtersStore = useFiltersStore();
const flightsFiltersStore = useFlightsFiltersStore();
const ferryFiltersStore = useFerryFiltersStore();
const browserStore = useBrowserStore();

const { t } = i18n.global;

//NOTE: duplicated
const currentSelectedOrderType = computed<
  AccommodationSelectorType | undefined
>(() => {
  if (props.segment.barType === ProductCardTopBarType.Selected) {
    return;
  }

  return props.segment.barType === ProductCardTopBarType.Suggestion
    ? 'popularAccommodation'
    : 'cheapestAccommodation';
});
function openHotelFilterPanel(source: 'from-plus' | 'from-more'): void {
  statsigEventsStore.logTripOverviewPreferencesModalOpened({ source });
  tripOverviewPanelsStore.openHotelFiltersPanel(props.segment.ingredientId);
}

function removeHotelFilter(id: HotelFilter): void {
  statsigEventsStore.logTripOverviewFiltersRemoved();
  filtersStore.removeFilterFromTripOverview(id, props.segment.ingredientId);
}

function openFlightFilterPanel(): void {
  flightsFiltersStore.setAllowedFlightIngredientIds([
    props.segment.ingredientId,
  ]);
  tripOverviewPanelsStore.openFlightsFiltersPanel();
}

function removeFlightFilter(tagId: string): void {
  flightsFiltersStore.removeFilter(tagId, props.segment.ingredientId);
}

function openFerryFilterModal(): void {
  tripOverviewPanelsStore.openFerryFiltersPanel(props.segment.ingredientId);
}

function removeFerryFilter(tagId: string): void {
  ferryFiltersStore.removeFilter(tagId, props.segment.ingredientId);
}
</script>

<template>
  <ItineraryTimeline :segment-type="props.segment.segmentType">
    <template #dropdown>
      <ItineraryTransportSelector
        v-if="isItineraryBookableTransportLoaderSegment(segment)"
        :transport-type="
          segment.segmentType === ItinerarySegmentType.Flight
            ? TransportType.Flight
            : TransportType.Ferry
        "
        :disabled="false"
        @select-transport="
          tripOverviewPageStore.selectTransport(segment, $event)
        "
      />
      <AccommodationSelector
        v-else-if="
          segment.segmentType === ItinerarySegmentType.Accommodation &&
          currentSelectedOrderType
        "
        :selected-option="currentSelectedOrderType"
        :disabled="false"
      />
      <AppSingleSelect
        v-else-if="segment.segmentType === ItinerarySegmentType.Activity"
        type="primary"
        :selected="{
          id: 1,
          label: t('planner.activity'),
          icon: 'activity',
        }"
        left-side-icon="activity"
        :options="[]"
        disabled
      />
    </template>
    <template #header>
      <AccommodationSegmentHeader
        v-if="props.segment.segmentType === ItinerarySegmentType.Accommodation"
        :segment="props.segment"
        :print-mode="false"
        @add-filter="openHotelFilterPanel('from-plus')"
        @remove-filter="removeHotelFilter"
        @show-more-filters="openHotelFilterPanel('from-more')"
      />
      <ActivityCardHeader
        v-if="props.segment.segmentType === ItinerarySegmentType.Activity"
        :print-mode="false"
        @remove="
          tripOverviewPageStore.deleteIngredientById(segment.ingredientId)
        "
      />
      <FlightSegmentHeader
        v-if="
          !browserStore.isSmallerThan1200px &&
          props.segment.segmentType === ItinerarySegmentType.Flight
        "
        :segment="props.segment"
        :print-mode="false"
        @show-more-filters="openFlightFilterPanel"
        @remove-filter="removeFlightFilter"
      />
      <FerrySegmentHeader
        v-if="props.segment.segmentType === ItinerarySegmentType.Ferry"
        :segment="props.segment"
        :print-mode="false"
        @show-more-filters="openFerryFilterModal"
        @remove-filter="removeFerryFilter"
      />
    </template>

    <template #large-header>
      <FlightSegmentHeader
        v-if="
          browserStore.isSmallerThan1200px &&
          props.segment.segmentType === ItinerarySegmentType.Flight
        "
        :segment="props.segment"
        :print-mode="false"
        @show-more-filters="openFlightFilterPanel"
        @remove-filter="removeFlightFilter"
      />
    </template>

    <template #content>
      <div class="product-card-loader">
        <div
          :class="['product-card-loader-content', [props.segment.segmentType]]"
        >
          <AppFlicker
            height="spacing-16"
            width="spacing-80"
            border-radius="border-radius-8"
          />
          <AppFlicker
            height="spacing-16"
            width="spacing-48"
            border-radius="border-radius-8"
          />
        </div>
        <ChangeProductLink
          v-if="
            props.segment.segmentType !== ItinerarySegmentType.Activity &&
            props.segment.segmentType !== ItinerarySegmentType.Accommodation
          "
          :link-context="{ segmentType: props.segment.segmentType }"
          class="change-product-link"
        />
        <ChangeProductLink
          v-if="
            props.segment.segmentType === ItinerarySegmentType.Activity ||
            props.segment.segmentType === ItinerarySegmentType.Accommodation
          "
          :link-context="{
            segmentType: props.segment.segmentType,
            ingredientIndex: props.segment.ingredientIndex,
          }"
          class="change-product-link"
        />
      </div>
    </template>
  </ItineraryTimeline>
</template>

<style scoped lang="scss">
.product-card-loader {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-16);

  .product-card-loader-content {
    padding: var(--spacing-16);
    background: var(--color-white);
    border: 1px solid var(--color-secondary-93);
    border-radius: var(--border-radius-12);

    display: flex;
    flex-flow: column;
    gap: var(--spacing-8);

    &.flight {
      height: 166px;
    }

    &.ferry {
      height: 168px;
    }

    &.accommodation {
      height: 380px;
    }

    &.activity {
      height: 302px;
    }

    @include for-less-or-equal-than-599-px {
      &.flight {
        height: 235px;
      }

      &.ferry {
        height: 322px;
      }

      &.accommodation {
        height: 479px;
      }

      &.activity {
        height: 367px;
      }
    }
  }

  .change-product-link {
    align-self: flex-end;
  }
}
</style>
